import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { cn } from '../lib/cn';
import {
  Plus,
  Search,
  Trash2,
  Edit,
  X,
  Loader2,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  MapPin,
  ArrowLeft,
  Grid3X3,
} from 'lucide-react';

// ============================================
// TYPES
// ============================================

interface Cemetery {
  id: string;
  name: string;
  address: string | null;
  city: string | null;
  state: string | null;
  contactInfo: string | null;
  notes: string | null;
  sections?: CemeterySection[];
}

interface CemeterySection {
  id: string;
  cemeteryId: string;
  name: string;
  rows: number | null;
  columns: number | null;
  description: string | null;
  _count?: { plots: number };
}

interface CemeteryPlot {
  id: string;
  sectionId: string;
  row: string;
  plotNumber: string;
  status: string;
  price: number | null;
  burial: {
    id: string;
    deceasedName: string;
    deceasedHebrewName: string | null;
    dateOfDeath: string | null;
    dateOfBurial: string | null;
  } | null;
}

interface Burial {
  id: string;
  deceasedName: string;
  deceasedHebrewName: string | null;
  memberId: string | null;
  dateOfDeath: string | null;
  dateOfBurial: string | null;
  hebrewDateOfDeath: string | null;
  notes: string | null;
  plot: {
    id: string;
    row: string;
    plotNumber: string;
    section: { id: string; name: string };
    cemetery: { id: string; name: string };
  };
  createdAt: string;
}

// ============================================
// UTILITY
// ============================================

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const plotStatusColors: Record<string, string> = {
  available: 'bg-green-200 border-green-400 text-green-800',
  reserved: 'bg-blue-200 border-blue-400 text-blue-800',
  occupied: 'bg-gray-300 border-gray-500 text-gray-800',
  maintenance: 'bg-yellow-200 border-yellow-400 text-yellow-800',
};

// ============================================
// MAIN PAGE
// ============================================

export function CemeteryPage() {
  const [activeTab, setActiveTab] = useState<'cemeteries' | 'burials' | 'search'>('cemeteries');

  const tabs = [
    { key: 'cemeteries' as const, label: 'Cemeteries', icon: MapPin },
    { key: 'burials' as const, label: 'Burials', icon: Grid3X3 },
    { key: 'search' as const, label: 'Search', icon: Search },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Cemetery Management</h1>
        <p className="mt-1 text-sm text-gray-500">
          Manage cemetery records, plots, and burial information
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={cn(
                'flex items-center gap-2 border-b-2 px-1 py-3 text-sm font-medium',
                activeTab === tab.key
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700'
              )}
            >
              <tab.icon className="h-4 w-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'cemeteries' && <CemeteriesTab />}
      {activeTab === 'burials' && <BurialsTab />}
      {activeTab === 'search' && <SearchTab />}
    </div>
  );
}

// ============================================
// TAB 1: CEMETERIES
// ============================================

function CemeteriesTab() {
  const queryClient = useQueryClient();
  const [selectedCemetery, setSelectedCemetery] = useState<string | null>(null);
  const [selectedSection, setSelectedSection] = useState<string | null>(null);
  const [showCemeteryModal, setShowCemeteryModal] = useState(false);
  const [editingCemetery, setEditingCemetery] = useState<Cemetery | null>(null);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState<CemeterySection | null>(null);
  const [showPlotsModal, setShowPlotsModal] = useState(false);
  const [selectedPlot, setSelectedPlot] = useState<CemeteryPlot | null>(null);
  const [showBurialModal, setShowBurialModal] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: string; id: string } | null>(null);

  // Fetch cemeteries
  const { data: cemeteries, isLoading: loadingCemeteries } = useQuery({
    queryKey: ['cemeteries'],
    queryFn: async () => {
      const response = await api.get<Cemetery[]>('/cemetery');
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
  });

  // Fetch selected cemetery detail
  const { data: cemeteryDetail } = useQuery({
    queryKey: ['cemetery', selectedCemetery],
    queryFn: async () => {
      if (!selectedCemetery) return null;
      const response = await api.get<Cemetery>(`/cemetery/${selectedCemetery}`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || null;
    },
    enabled: !!selectedCemetery,
  });

  // Fetch plots for selected section
  const { data: plots } = useQuery({
    queryKey: ['cemetery-plots', selectedSection],
    queryFn: async () => {
      if (!selectedSection) return [];
      const response = await api.get<CemeteryPlot[]>(`/cemetery/sections/${selectedSection}/plots`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: !!selectedSection,
  });

  // Cemetery CRUD
  const createCemeteryMutation = useMutation({
    mutationFn: async (data: Partial<Cemetery>) => {
      const response = await api.post<Cemetery>('/cemetery', data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemeteries'] });
      setShowCemeteryModal(false);
      setEditingCemetery(null);
    },
  });

  const updateCemeteryMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Cemetery> }) => {
      const response = await api.patch<Cemetery>(`/cemetery/${id}`, data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemeteries'] });
      queryClient.invalidateQueries({ queryKey: ['cemetery', selectedCemetery] });
      setShowCemeteryModal(false);
      setEditingCemetery(null);
    },
  });

  const deleteCemeteryMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/cemetery/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemeteries'] });
      setSelectedCemetery(null);
      setDeleteConfirm(null);
    },
  });

  // Section CRUD
  const createSectionMutation = useMutation({
    mutationFn: async (data: Partial<CemeterySection>) => {
      const response = await api.post<CemeterySection>(`/cemetery/${selectedCemetery}/sections`, data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemetery', selectedCemetery] });
      setShowSectionModal(false);
      setEditingSection(null);
    },
  });

  const updateSectionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<CemeterySection> }) => {
      const response = await api.patch<CemeterySection>(`/cemetery/sections/${id}`, data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemetery', selectedCemetery] });
      setShowSectionModal(false);
      setEditingSection(null);
    },
  });

  const deleteSectionMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/cemetery/sections/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemetery', selectedCemetery] });
      setSelectedSection(null);
      setDeleteConfirm(null);
    },
  });

  // Bulk create plots
  const createPlotsMutation = useMutation({
    mutationFn: async (data: { rows: string[]; plotsPerRow: number }) => {
      const response = await api.post(`/cemetery/sections/${selectedSection}/plots`, data);
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemetery-plots', selectedSection] });
      queryClient.invalidateQueries({ queryKey: ['cemetery', selectedCemetery] });
      setShowPlotsModal(false);
    },
  });

  if (loadingCemeteries) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Plot grid view
  if (selectedSection) {
    const section = cemeteryDetail?.sections?.find((s) => s.id === selectedSection);
    const groupedByRow: Record<string, CemeteryPlot[]> = {};
    if (plots) {
      for (const p of plots) {
        if (!groupedByRow[p.row]) groupedByRow[p.row] = [];
        groupedByRow[p.row].push(p);
      }
    }
    const rows = Object.keys(groupedByRow).sort();

    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedSection(null)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to sections
          </button>
          <h3 className="font-semibold text-gray-900">
            Section: {section?.name}
          </h3>
          <button
            onClick={() => setShowPlotsModal(true)}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Plots
          </button>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-xs">
          <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-green-200 border border-green-400" /> Available</span>
          <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-blue-200 border border-blue-400" /> Reserved</span>
          <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-gray-300 border border-gray-500" /> Occupied</span>
          <span className="flex items-center gap-1"><span className="inline-block w-4 h-4 rounded bg-yellow-200 border border-yellow-400" /> Maintenance</span>
        </div>

        {/* Plot Grid */}
        <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-6">
          {rows.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No plots in this section. Click "Add Plots" to create them.
            </div>
          ) : (
            <div className="space-y-3">
              {rows.map((row) => (
                <div key={row} className="flex items-center gap-2">
                  <span className="w-10 text-xs font-medium text-gray-500 text-right">Row {row}</span>
                  <div className="flex gap-1 flex-wrap">
                    {groupedByRow[row]
                      .sort((a, b) => parseInt(a.plotNumber) - parseInt(b.plotNumber))
                      .map((plot) => (
                        <button
                          key={plot.id}
                          onClick={() => {
                            setSelectedPlot(plot);
                            if (plot.status === 'available' || plot.status === 'reserved') {
                              setShowBurialModal(true);
                            }
                          }}
                          className={cn(
                            'w-10 h-10 rounded border text-xs font-medium flex items-center justify-center hover:opacity-80 transition-opacity',
                            plotStatusColors[plot.status] || 'bg-gray-100 border-gray-300 text-gray-600'
                          )}
                          title={plot.burial
                            ? `${plot.burial.deceasedName} (${plot.row}-${plot.plotNumber})`
                            : `${plot.row}-${plot.plotNumber} (${plot.status})`
                          }
                        >
                          {plot.plotNumber}
                        </button>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Plot detail / burial info */}
        {selectedPlot && selectedPlot.burial && !showBurialModal && (
          <div className="rounded-xl bg-white shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="font-semibold text-gray-900">
                Plot {selectedPlot.row}-{selectedPlot.plotNumber} - Burial Record
              </h4>
              <button onClick={() => setSelectedPlot(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-gray-500">Deceased Name</dt>
                <dd className="font-medium text-gray-900">{selectedPlot.burial.deceasedName}</dd>
              </div>
              {selectedPlot.burial.deceasedHebrewName && (
                <div>
                  <dt className="text-gray-500">Hebrew Name</dt>
                  <dd className="font-medium text-gray-900">{selectedPlot.burial.deceasedHebrewName}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500">Date of Death</dt>
                <dd className="text-gray-900">{formatDate(selectedPlot.burial.dateOfDeath)}</dd>
              </div>
              <div>
                <dt className="text-gray-500">Date of Burial</dt>
                <dd className="text-gray-900">{formatDate(selectedPlot.burial.dateOfBurial)}</dd>
              </div>
            </dl>
          </div>
        )}

        {/* Add Plots Modal */}
        {showPlotsModal && (
          <BulkPlotsModal
            onClose={() => setShowPlotsModal(false)}
            onSubmit={(data) => createPlotsMutation.mutate(data)}
            isLoading={createPlotsMutation.isPending}
          />
        )}

        {/* Burial Assignment Modal */}
        {showBurialModal && selectedPlot && (
          <BurialFormModal
            plotId={selectedPlot.id}
            plotLabel={`${selectedPlot.row}-${selectedPlot.plotNumber}`}
            onClose={() => {
              setShowBurialModal(false);
              setSelectedPlot(null);
            }}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['cemetery-plots', selectedSection] });
              setShowBurialModal(false);
              setSelectedPlot(null);
            }}
          />
        )}
      </div>
    );
  }

  // Sections list view
  if (selectedCemetery && cemeteryDetail) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <button
            onClick={() => setSelectedCemetery(null)}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to cemeteries
          </button>
          <h3 className="font-semibold text-gray-900">{cemeteryDetail.name}</h3>
          <button
            onClick={() => setShowSectionModal(true)}
            className="ml-auto flex items-center gap-1.5 rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Section
          </button>
        </div>

        {cemeteryDetail.address && (
          <p className="text-sm text-gray-500">
            {[cemeteryDetail.address, cemeteryDetail.city, cemeteryDetail.state].filter(Boolean).join(', ')}
          </p>
        )}

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {(!cemeteryDetail.sections || cemeteryDetail.sections.length === 0) ? (
            <div className="col-span-full text-center py-8 text-gray-500 rounded-xl bg-white shadow-sm border border-gray-200">
              No sections yet. Click "Add Section" to create one.
            </div>
          ) : (
            cemeteryDetail.sections.map((section) => (
              <div
                key={section.id}
                className="rounded-xl bg-white shadow-sm border border-gray-200 p-4 hover:border-primary-300 cursor-pointer transition-colors"
                onClick={() => setSelectedSection(section.id)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{section.name}</h4>
                  <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => {
                        setEditingSection(section);
                        setShowSectionModal(true);
                      }}
                      className="p-1 text-gray-400 hover:text-primary-600"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm({ type: 'section', id: section.id })}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
                {section.description && (
                  <p className="text-sm text-gray-500 mb-2">{section.description}</p>
                )}
                <p className="text-xs text-gray-400">
                  {section._count?.plots || 0} plots
                </p>
              </div>
            ))
          )}
        </div>

        {/* Section Modal */}
        {showSectionModal && (
          <SectionModal
            section={editingSection}
            onClose={() => {
              setShowSectionModal(false);
              setEditingSection(null);
            }}
            onSubmit={(data) => {
              if (editingSection) {
                updateSectionMutation.mutate({ id: editingSection.id, data });
              } else {
                createSectionMutation.mutate(data);
              }
            }}
            isLoading={createSectionMutation.isPending || updateSectionMutation.isPending}
          />
        )}

        {/* Delete Confirmation */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Delete {deleteConfirm.type === 'section' ? 'Section' : 'Cemetery'}</h3>
                  <p className="text-sm text-gray-500">This action cannot be undone.</p>
                </div>
              </div>
              {(deleteSectionMutation.isError || deleteCemeteryMutation.isError) && (
                <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                  {((deleteSectionMutation.error || deleteCemeteryMutation.error) as Error)?.message}
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (deleteConfirm.type === 'section') {
                      deleteSectionMutation.mutate(deleteConfirm.id);
                    } else {
                      deleteCemeteryMutation.mutate(deleteConfirm.id);
                    }
                  }}
                  disabled={deleteSectionMutation.isPending || deleteCemeteryMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Cemetery list view
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button
          onClick={() => {
            setEditingCemetery(null);
            setShowCemeteryModal(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
        >
          <Plus className="h-4 w-4" />
          Add Cemetery
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {!cemeteries || cemeteries.length === 0 ? (
          <div className="col-span-full text-center py-12 text-gray-500 rounded-xl bg-white shadow-sm border border-gray-200">
            <MapPin className="h-8 w-8 mx-auto mb-2 text-gray-300" />
            <p>No cemeteries configured yet.</p>
          </div>
        ) : (
          cemeteries.map((cemetery) => (
            <div
              key={cemetery.id}
              className="rounded-xl bg-white shadow-sm border border-gray-200 p-5 hover:border-primary-300 cursor-pointer transition-colors"
              onClick={() => setSelectedCemetery(cemetery.id)}
            >
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-semibold text-gray-900">{cemetery.name}</h3>
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => {
                      setEditingCemetery(cemetery);
                      setShowCemeteryModal(true);
                    }}
                    className="p-1 text-gray-400 hover:text-primary-600"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm({ type: 'cemetery', id: cemetery.id })}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {cemetery.address && (
                <p className="text-sm text-gray-500 flex items-center gap-1">
                  <MapPin className="h-3.5 w-3.5 flex-shrink-0" />
                  {[cemetery.address, cemetery.city, cemetery.state].filter(Boolean).join(', ')}
                </p>
              )}
              {cemetery.contactInfo && (
                <p className="text-sm text-gray-400 mt-1">{cemetery.contactInfo}</p>
              )}
            </div>
          ))
        )}
      </div>

      {/* Cemetery Modal */}
      {showCemeteryModal && (
        <CemeteryModal
          cemetery={editingCemetery}
          onClose={() => {
            setShowCemeteryModal(false);
            setEditingCemetery(null);
          }}
          onSubmit={(data) => {
            if (editingCemetery) {
              updateCemeteryMutation.mutate({ id: editingCemetery.id, data });
            } else {
              createCemeteryMutation.mutate(data);
            }
          }}
          isLoading={createCemeteryMutation.isPending || updateCemeteryMutation.isPending}
        />
      )}

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Cemetery</h3>
                <p className="text-sm text-gray-500">This action cannot be undone.</p>
              </div>
            </div>
            {deleteCemeteryMutation.isError && (
              <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-700">
                {(deleteCemeteryMutation.error as Error).message}
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteCemeteryMutation.mutate(deleteConfirm.id)}
                disabled={deleteCemeteryMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// MODALS
// ============================================

function CemeteryModal({
  cemetery,
  onClose,
  onSubmit,
  isLoading,
}: {
  cemetery: Cemetery | null;
  onClose: () => void;
  onSubmit: (data: Partial<Cemetery>) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    name: cemetery?.name || '',
    address: cemetery?.address || '',
    city: cemetery?.city || '',
    state: cemetery?.state || '',
    contactInfo: cemetery?.contactInfo || '',
    notes: cemetery?.notes || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      address: formData.address || undefined,
      city: formData.city || undefined,
      state: formData.state || undefined,
      contactInfo: formData.contactInfo || undefined,
      notes: formData.notes || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {cemetery ? 'Edit Cemetery' : 'New Cemetery'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Contact Info</label>
            <input
              type="text"
              value={formData.contactInfo}
              onChange={(e) => setFormData({ ...formData, contactInfo: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
              {isLoading ? 'Saving...' : cemetery ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function SectionModal({
  section,
  onClose,
  onSubmit,
  isLoading,
}: {
  section: CemeterySection | null;
  onClose: () => void;
  onSubmit: (data: Partial<CemeterySection>) => void;
  isLoading: boolean;
}) {
  const [formData, setFormData] = useState({
    name: section?.name || '',
    rows: section?.rows?.toString() || '',
    columns: section?.columns?.toString() || '',
    description: section?.description || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name: formData.name,
      rows: formData.rows ? parseInt(formData.rows) : undefined,
      columns: formData.columns ? parseInt(formData.columns) : undefined,
      description: formData.description || undefined,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {section ? 'Edit Section' : 'New Section'}
          </h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="e.g., Section A, Main Area"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Rows</label>
              <input
                type="number"
                value={formData.rows}
                onChange={(e) => setFormData({ ...formData, rows: e.target.value })}
                min="1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Columns</label>
              <input
                type="number"
                value={formData.columns}
                onChange={(e) => setFormData({ ...formData, columns: e.target.value })}
                min="1"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
              {isLoading ? 'Saving...' : section ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BulkPlotsModal({
  onClose,
  onSubmit,
  isLoading,
}: {
  onClose: () => void;
  onSubmit: (data: { rows: string[]; plotsPerRow: number }) => void;
  isLoading: boolean;
}) {
  const [rowLetters, setRowLetters] = useState('A, B, C');
  const [plotsPerRow, setPlotsPerRow] = useState('10');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const rows = rowLetters.split(',').map((r) => r.trim()).filter(Boolean);
    onSubmit({
      rows,
      plotsPerRow: parseInt(plotsPerRow) || 10,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Add Plots</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Row Labels (comma separated)</label>
            <input
              type="text"
              value={rowLetters}
              onChange={(e) => setRowLetters(e.target.value)}
              placeholder="A, B, C, D"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
            <p className="text-xs text-gray-400 mt-1">Creates rows with these labels</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Plots per Row</label>
            <input
              type="number"
              value={plotsPerRow}
              onChange={(e) => setPlotsPerRow(e.target.value)}
              min="1"
              max="100"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <p className="text-sm text-gray-500">
            This will create {rowLetters.split(',').filter((r) => r.trim()).length * (parseInt(plotsPerRow) || 0)} plots total. Existing plots will not be duplicated.
          </p>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={isLoading} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
              {isLoading ? 'Creating...' : 'Create Plots'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function BurialFormModal({
  plotId,
  plotLabel,
  onClose,
  onSuccess,
}: {
  plotId: string;
  plotLabel: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [formData, setFormData] = useState({
    deceasedName: '',
    deceasedHebrewName: '',
    dateOfDeath: '',
    dateOfBurial: '',
    hebrewDateOfDeath: '',
    notes: '',
  });

  const createBurialMutation = useMutation({
    mutationFn: async () => {
      const response = await api.post('/cemetery/burials', {
        plotId,
        deceasedName: formData.deceasedName,
        deceasedHebrewName: formData.deceasedHebrewName || undefined,
        dateOfDeath: formData.dateOfDeath || undefined,
        dateOfBurial: formData.dateOfBurial || undefined,
        hebrewDateOfDeath: formData.hebrewDateOfDeath || undefined,
        notes: formData.notes || undefined,
      });
      if (!response.success) throw new Error(response.error?.message);
      return response.data;
    },
    onSuccess: () => {
      onSuccess();
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createBurialMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl max-w-md w-full">
        <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Record Burial - Plot {plotLabel}</h2>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Deceased Name *</label>
            <input
              type="text"
              value={formData.deceasedName}
              onChange={(e) => setFormData({ ...formData, deceasedName: e.target.value })}
              required
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hebrew Name</label>
            <input
              type="text"
              value={formData.deceasedHebrewName}
              onChange={(e) => setFormData({ ...formData, deceasedHebrewName: e.target.value })}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Death</label>
              <input
                type="date"
                value={formData.dateOfDeath}
                onChange={(e) => setFormData({ ...formData, dateOfDeath: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Burial</label>
              <input
                type="date"
                value={formData.dateOfBurial}
                onChange={(e) => setFormData({ ...formData, dateOfBurial: e.target.value })}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Hebrew Date of Death</label>
            <input
              type="text"
              value={formData.hebrewDateOfDeath}
              onChange={(e) => setFormData({ ...formData, hebrewDateOfDeath: e.target.value })}
              placeholder="e.g., 15 Tishrei 5784"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={2}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>

          {createBurialMutation.isError && (
            <div className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
              {(createBurialMutation.error as Error).message}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg">
              Cancel
            </button>
            <button type="submit" disabled={createBurialMutation.isPending} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg disabled:opacity-50">
              {createBurialMutation.isPending ? 'Recording...' : 'Record Burial'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ============================================
// TAB 2: BURIALS
// ============================================

function BurialsTab() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['cemetery-burials', page, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
      });
      if (search) params.append('search', search);
      const response = await api.get<Burial[]>(`/cemetery/burials?${params}`);
      if (!response.success) throw new Error(response.error?.message);
      return { burials: response.data || [], meta: response.meta };
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/cemetery/burials/${id}`);
      if (!response.success) throw new Error(response.error?.message);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cemetery-burials'] });
      setDeleteConfirm(null);
    },
  });

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by deceased name..."
          className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
        />
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Deceased Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Hebrew Name</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cemetery</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Section / Plot</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date of Death</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date of Burial</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {!data?.burials || data.burials.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        No burial records found
                      </td>
                    </tr>
                  ) : (
                    data.burials.map((burial) => (
                      <tr key={burial.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{burial.deceasedName}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{burial.deceasedHebrewName || '-'}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{burial.plot.cemetery.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {burial.plot.section.name} / {burial.plot.row}-{burial.plot.plotNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(burial.dateOfDeath)}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">{formatDate(burial.dateOfBurial)}</td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => setDeleteConfirm(burial.id)}
                            className="p-1 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {data?.meta && data.meta.totalPages > 1 && (
              <div className="flex items-center justify-between border-t border-gray-200 bg-gray-50 px-6 py-3">
                <p className="text-sm text-gray-500">
                  Page {data.meta.page} of {data.meta.totalPages} ({data.meta.total} records)
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 1}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= (data.meta?.totalPages || 1)}
                    className="p-1 rounded text-gray-400 hover:text-gray-600 disabled:opacity-50"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Delete Burial Record</h3>
                <p className="text-sm text-gray-500">This will also mark the plot as available.</p>
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteMutation.mutate(deleteConfirm)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// TAB 3: SEARCH
// ============================================

function SearchTab() {
  const [query, setQuery] = useState('');

  const { data: results, isLoading } = useQuery({
    queryKey: ['cemetery-search', query],
    queryFn: async () => {
      if (!query || query.length < 2) return [];
      const response = await api.get<Burial[]>(`/cemetery/burials?search=${encodeURIComponent(query)}&limit=50`);
      if (!response.success) throw new Error(response.error?.message);
      return response.data || [];
    },
    enabled: query.length >= 2,
  });

  return (
    <div className="space-y-6">
      {/* Search Bar */}
      <div className="relative max-w-2xl mx-auto">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search burial records by name..."
          className="w-full pl-12 pr-4 py-3 text-base rounded-xl border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
        />
      </div>

      {/* Results */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
        </div>
      )}

      {query.length >= 2 && !isLoading && results && results.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          No records found for "{query}"
        </div>
      )}

      {results && results.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 max-w-4xl mx-auto">
          {results.map((burial) => (
            <div
              key={burial.id}
              className="rounded-xl bg-white shadow-sm border border-gray-200 p-5"
            >
              <h3 className="font-semibold text-gray-900">{burial.deceasedName}</h3>
              {burial.deceasedHebrewName && (
                <p className="text-gray-600 text-sm">{burial.deceasedHebrewName}</p>
              )}
              <div className="mt-3 space-y-1 text-sm">
                <p className="flex items-center gap-2 text-gray-500">
                  <MapPin className="h-3.5 w-3.5" />
                  {burial.plot.cemetery.name} - {burial.plot.section.name}, Row {burial.plot.row}, Plot {burial.plot.plotNumber}
                </p>
                {burial.dateOfDeath && (
                  <p className="text-gray-500">Passed: {formatDate(burial.dateOfDeath)}</p>
                )}
                {burial.dateOfBurial && (
                  <p className="text-gray-500">Buried: {formatDate(burial.dateOfBurial)}</p>
                )}
                {burial.hebrewDateOfDeath && (
                  <p className="text-gray-500">Hebrew Date: {burial.hebrewDateOfDeath}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
